export type Article = {
  slug: string
  category: string
  date: string
  title: string
  standfirst: string
  author: string
  image: string
  body: string[]
  pullQuote?: string
  tags: string[]
}

const rally =
  'https://cdn.magicpatterns.com/patterns/generated-images/23fed40f-ca99-4026-a7ba-476691c2f191.jpg'
const power =
  'https://cdn.magicpatterns.com/patterns/generated-images/78758787-cb47-4c4a-80e5-daa7f359f8d3.jpg'
const volunteers =
  'https://cdn.magicpatterns.com/patterns/generated-images/ebf27f4b-fec1-4388-bbbb-126b7ec20001.jpg'
const townhall =
  'https://cdn.magicpatterns.com/patterns/generated-images/93340211-d03b-4760-9807-29ce94192f52.jpg'
const students =
  'https://cdn.magicpatterns.com/patterns/generated-images/db019d07-84c7-4add-8953-0634ea68b0d3.jpg'
const farm =
  'https://cdn.magicpatterns.com/patterns/generated-images/e8e04d27-df9c-4881-b697-26bf91fc9f04.jpg'

export const newsCategories = [
  'Latest',
  'Campaign Trail',
  'Statements',
  'Press Releases',
  'Speeches',
  'Interviews',
  'Policy',
]

export const articles: Article[] = [
  {
    slug: 'national-economic-summit-address',
    category: 'Speeches',
    date: '18 August 2026',
    title:
      'Full address: “Nigeria does not have a talent problem. It has a delivery problem.”',
    standfirst:
      'Speaking to 4,000 delegates at the National Economic Summit in Abuja, Adebayo S. Bello set out the case for a government measured on published targets rather than announcements.',
    author: 'Campaign Communications',
    image: rally,
    pullQuote:
      'Do not ask me whether I care about your community. Ask me what I have published about it, and check whether it happened.',
    tags: ['Economy', 'Governance', 'Accountability'],
    body: [
      'Thank you for the invitation, and for the patience of a room that has heard a great many speeches about the Nigerian economy.',
      'I want to make a narrow argument this morning. It is not that Nigeria lacks ideas. Almost every problem we face has been diagnosed, in this hall, by people in this room. It is that we have built a state which can announce a policy but cannot deliver one, and we have grown used to treating the announcement as the achievement.',
      'When I took office in Osun in 2015, the state owed eleven months of salaries. There was no shortage of plans. What was missing was a named person, a published date and a consequence. We built those three things first, and everything else followed from them.',
      'So the commitment I make is procedural before it is ideological. Every minister in my administration will sign a performance contract that is published on the day they are appointed. Every federal contract above one hundred million naira will be published within thirty days of award, with beneficial ownership attached. And at one hundred days, we will publish an independently verified scorecard — including the targets we missed.',
      'That is not a slogan. It is a standard, and I am asking Nigerians to hold this campaign to it from today, not from inauguration.',
    ],
  },
  {
    slug: 'costed-plan-estimated-billing',
    category: 'Policy',
    date: '15 August 2026',
    title: 'Bello releases costed plan to end estimated electricity billing',
    standfirst:
      'The 24-page document sets a two-year deadline for universal metering and names the funding source for each phase.',
    author: 'Policy Directorate',
    image: power,
    tags: ['Power', 'Consumer protection'],
    body: [
      'The campaign has published a costed plan to end estimated billing across Nigeria within two years of inauguration, financed against the reduction in collection losses rather than new borrowing.',
      'Under the plan, distribution companies would face published performance deadlines for meter deployment, with licence review for those that miss them. A national dashboard would report metering coverage, hours of supply per feeder and aggregate losses monthly, by state.',
      '“Estimated billing is a tax on honesty,” the candidate said in Abuja. “The customer who pays is charged for the customer who does not, and for the power that never arrived. That ends.”',
      'The document is available in full on the manifesto centre, alongside the underlying cost model.',
    ],
  },
  {
    slug: 'three-days-north-west',
    category: 'Campaign Trail',
    date: '12 August 2026',
    title: 'Three days in the North West: Kano, Katsina and Sokoto',
    standfirst:
      'Meetings with textile employers, displaced farming families and the Kano State teachers’ union.',
    author: 'Campaign Trail Desk',
    image: volunteers,
    tags: ['North West', 'Jobs', 'Security'],
    body: [
      'The campaign spent three days across the North West this week, beginning with a closed-door session with textile and leather manufacturers in Kano.',
      'In Katsina, the candidate met families displaced from farming communities in the state’s western corridor, and repeated the campaign’s commitment to a dedicated rural security corps recruited locally and deployed within the first sixty days.',
      'The visit closed in Sokoto with a meeting on almajiri education reform, where state officials pressed for federal co-funding tied to verified enrolment rather than headcount estimates.',
    ],
  },
  {
    slug: 'statement-on-benue-attacks',
    category: 'Statements',
    date: '09 August 2026',
    title: 'On the Benue attacks: “Condolences are not a security policy”',
    standfirst:
      'The campaign restates its commitment to a dedicated rural security corps within the first sixty days.',
    author: 'Office of the Candidate',
    image: townhall,
    tags: ['Security', 'Benue'],
    body: [
      'Our condolences go to the families in Guma and Logo local government areas who buried their relatives this week. They have heard condolences before. They are entitled to something else.',
      'Nigeria does not lack the capacity to protect farming communities. It lacks a standing structure whose specific job that is, with a named commander and a published response standard.',
      'This campaign has committed to recruiting and training the first twelve thousand officers of a rural security corps within the first hundred days, drawn from the communities they will protect and operating under formal command and human rights accountability.',
    ],
  },
  {
    slug: 'press-release-volunteer-milestone',
    category: 'Press Releases',
    date: '05 August 2026',
    title: 'Campaign passes 400,000 verified volunteers across all 774 LGAs',
    standfirst:
      'Volunteer registration is now verified by phone number and polling unit, with published totals by state.',
    author: 'Campaign Communications',
    image: volunteers,
    tags: ['Volunteers', 'Organisation'],
    body: [
      'The campaign confirmed today that verified volunteer registration has passed 400,000 people, with at least one active coordinator in each of Nigeria’s 774 local government areas.',
      'Every registration is verified against a phone number and a polling unit, and state-level totals are published weekly on the campaign website to allow independent scrutiny.',
      'Volunteer training begins this month in eighteen states, focused on voter education, polling unit support and countering misinformation.',
    ],
  },
  {
    slug: 'interview-on-education-financing',
    category: 'Interviews',
    date: '01 August 2026',
    title: '“Enrolment is not learning”: an interview on education financing',
    standfirst:
      'The candidate on payroll audits, technical colleges, and why federal money should follow verified outcomes.',
    author: 'Channels Sunday Politics',
    image: students,
    tags: ['Education', 'Financing'],
    body: [
      'Asked why he begins with a payroll audit rather than new schools, the candidate was blunt: “Because we are paying people who do not teach, in states that cannot tell us how many teachers they have. You cannot fix what you refuse to count.”',
      'He committed to tying federal education transfers to published enrolment, attendance and learning assessment data, verified independently, and to rebuilding ten thousand schools prioritised by an open needs register.',
      'On technical education, he argued that Nigeria has “spent forty years treating the trades as a consolation prize” and pledged employer partnerships with guaranteed placement interviews on completion.',
    ],
  },
  {
    slug: 'food-corridor-security-plan',
    category: 'Policy',
    date: '28 July 2026',
    title: 'Campaign publishes food corridor security and mechanisation map',
    standfirst:
      'The map sets out where the rural security corps deploys first and where LGA mechanisation service points open.',
    author: 'Policy Directorate',
    image: farm,
    tags: ['Agriculture', 'Security', 'Food prices'],
    body: [
      'The campaign has published a corridor-by-corridor map showing the sequence of rural security deployment alongside the rollout of local mechanisation service points.',
      'The sequencing is deliberate: equipment follows protection. “There is no point financing a tractor for a farm nobody can safely reach,” the policy directorate noted.',
      'Targets include cutting post-harvest losses below ten per cent and returning to cultivation land taken out of use by insecurity.',
    ],
  },
]

export function getArticle(slug: string): Article | undefined {
  return articles.find((article) => article.slug === slug)
}
